package com.yendo.mx.ui.restaurant

import com.yendo.mx.utils.BackHandler
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material.icons.filled.KeyboardArrowDown
import androidx.compose.material.icons.filled.LocalOffer
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Star
import androidx.compose.material.icons.filled.Storefront
import androidx.compose.material.icons.filled.TwoWheeler
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.*
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.material3.pulltorefresh.PullToRefreshDefaults
import androidx.compose.material3.pulltorefresh.rememberPullToRefreshState
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import com.yendo.mx.models.Producto
import com.yendo.mx.models.RestauranteFirebase
import com.yendo.mx.models.CartManager
import com.yendo.mx.repository.ProductoRepository
import com.yendo.mx.ui.components.BotonCarritoFlotante
import com.yendo.mx.ui.components.RestauranteRatingBadge
import com.yendo.mx.ui.theme.RosaYendo
import com.yendo.mx.ui.theme.YendoGradient
import com.yendo.mx.utils.validarRestauranteAbierto
import com.yendo.mx.utils.ConfigManager
import com.yendo.mx.utils.formatDecimal
import com.yendo.mx.utils.AdAnalyticsManager
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

// IMPORTS PARA FECHAS Y HORARIOS (KMP)
import com.yendo.mx.utils.getAhora
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.datetime.DayOfWeek

import coil3.compose.AsyncImage
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.crossfade
import androidx.compose.ui.graphics.FilterQuality

@OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)
@Composable
fun DetalleRestaurante(
    restaurante: RestauranteFirebase,
    productoInicial: Producto? = null,
    onBack: () -> Unit,
    onVerCarrito: (String) -> Unit,
    viewModel: RestauranteViewModel = viewModel { RestauranteViewModel() }
) {
    // 🚀 COLORES DARK MODE
    val isDark = isSystemInDarkTheme()
    val colorFondoPantalla = if (isDark) Color(0xFF121212) else Color.White
    val colorTextoPrincipal = if (isDark) Color.White else Color.Black
    val colorTextoSecundario = if (isDark) Color(0xFFAAAAAA) else Color.Gray
    val colorFondoBotonBack = if (isDark) Color(0xFF2C2C2E) else Color.White
    val colorFondoSearch = if (isDark) Color(0xFF1E1E1E) else Color(0xFFF9F9F9)
    val colorBordeSearch = if (isDark) Color(0xFF3A3A3C) else Color(0xFFE0E0E0)
    val colorChipInactivo = if (isDark) Color(0xFF2C2C2E) else Color(0xFFF5F5F5)
    val colorFondoDialogo = if (isDark) Color(0xFF1E1E1E) else Color.White

    val colorFondoAlertaPausado = if (isDark) Color(0xFF3E2723) else Color(0xFFFFF0F0)
    val colorBordeAlertaPausado = if (isDark) Color(0xFF5D4037) else Color(0xFFFFCDD2)
    val colorFondoAlertaInfo = if (isDark) Color(0xFF0D253F) else Color(0xFFE3F2FD)
    val colorBordeAlertaInfo = if (isDark) Color(0xFF1976D2).copy(alpha = 0.3f) else Color(0xFF90CAF9)

    // 🚀 BACK HANDLER LOCAL
    BackHandler { onBack() }

    val productoParaAdicionales = remember { mutableStateOf<Producto?>(null) }
    val searchQuery = remember { mutableStateOf("") }
    val productoPendienteCambio = remember { mutableStateOf<Producto?>(null) }
    var mostrarMenuCategorias by remember { mutableStateOf(false) }

    var isRefreshing by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val refreshState = rememberPullToRefreshState()

    val esComercio = restaurante.infoPublica.tipoNegocio == "COMERCIO" || restaurante.infoPublica.tipoNegocio == "TIENDITA"

    // 🚀 CARGA DE DATOS EFICIENTE: Ya no cargamos todo de golpe para NINGÚN negocio.
    // La carga se dispara individualmente por categoría al entrar en pantalla (Scroll-Lazy).

    val productosMenu by viewModel.allProducts.collectAsState()
    val resultadosBusquedaAlgolia by viewModel.searchResults.collectAsState()
    val isSearchingAlgolia by viewModel.isSearching.collectAsState()

    // 🚀 DISPARADOR DE BÚSQUEDA ALGOLIA (Anti-Costos Firestore)
    LaunchedEffect(searchQuery.value) {
        if (searchQuery.value.length >= 3) {
            // 🚀 DEBOUNCE: Esperamos 300ms a que el usuario deje de escribir para no saturar Algolia
            delay(300)
            // 🚀 USAMOS EL ID DE ZONA DEL RESTAURANTE (Fuente de verdad para sus productos)
            viewModel.buscarProductos(searchQuery.value, restaurante.id, restaurante.ubicacion.idZona)
        }
    }

    // 🚀 UNIFICADOR DE PRODUCTOS PARA BÚSQUEDA
    val productosParaBusqueda = remember(productosMenu, resultadosBusquedaAlgolia, searchQuery.value) {
        if (searchQuery.value.isBlank()) emptyList()
        else (productosMenu + resultadosBusquedaAlgolia).distinctBy { it.id }
    }

    // 🚀 CATEGORÍAS DISPONIBLES (Respeta el orden asignado por el negocio y oculta las vacías)
    val categoryStatesList = viewModel.categoryStates.values.map { it.products.size to it.isLoading }
    val categoriasKeys = remember(restaurante, categoryStatesList, productosMenu, esComercio) {
        // 1. Respetamos el ORDEN EXACTO asignado en restaurante.categoriasMenu
        val rawKeys = if (restaurante.categoriasMenu.isNotEmpty()) {
            restaurante.categoriasMenu.map { it.trim() }.filter { it.isNotBlank() }.distinct()
        } else {
            productosMenu.map { it.infoProducto.categoria.trim() }.filter { it.isNotBlank() }.distinct()
        }

        if (rawKeys.isEmpty()) {
            listOf("Menú")
        } else {
            // 2. Filtramos únicamente las categorías que ya cargaron Y explícitamente terminaron sin productos
            val filtradas = rawKeys.filter { cat ->
                val state = viewModel.obtenerEstadoCategoria(restaurante.id, cat)
                val yaConsultado = viewModel.hasCategoryFetched(restaurante.id, cat)
                if (yaConsultado && !state.isLoading && state.products.isEmpty()) {
                    false
                } else {
                    true
                }
            }
            filtradas.ifEmpty { rawKeys }
        }
    }

    // 🚀 ELIMINADO: Ya no agrupamos localmente, usamos el flujo paginado del ViewModel.

    // 🚀 ESTADOS DE SCROLL
    val listState = rememberLazyListState()
    val tabsListState = rememberLazyListState()
    var categoriaSeleccionada by remember { mutableStateOf(categoriasKeys.firstOrNull() ?: "Menú") }

    val categoryIndices = remember(categoriasKeys, esComercio) {
        val map = mutableMapOf<String, Int>()
        var currentIndex = 1 // El header global es index 0
        
        // 🚀 ESTIMACIÓN DE ÍNDICES: 
        // Nota: En modo paginado, el scroll exacto es difícil de predecir.
        // Mapeamos al menos el inicio de cada sección.
        categoriasKeys.forEach { cat ->
            map[cat] = currentIndex
            currentIndex += if (esComercio) 1 else 5 // Estimamos 5 filas por restaurante
        }
        map
    }

    val firstVisibleIndex by remember { derivedStateOf { listState.firstVisibleItemIndex } }
    val isScrolling by remember { derivedStateOf { listState.isScrollInProgress } }

    LaunchedEffect(firstVisibleIndex) {
        if (isScrolling && searchQuery.value.isBlank()) {
            val visibleCat = categoryIndices.entries.findLast { it.value <= firstVisibleIndex }?.key
            if (visibleCat != null && visibleCat != categoriaSeleccionada) {
                categoriaSeleccionada = visibleCat
                val tabIdx = categoriasKeys.indexOf(visibleCat)
                if (tabIdx >= 0) tabsListState.animateScrollToItem(tabIdx)
            }
        }
    }

    val zonaConfig = remember { ConfigManager.configActiva }
    val globalProgramadoActivo = zonaConfig?.operacion?.programados_activos ?: true
    val tiempoActualMs = remember { getAhora() }
    var restauranteFull by remember(restaurante.id) { mutableStateOf(restaurante) }
    val restActivo = restauranteFull

    val pausaHasta = restActivo.operacion.pausaHasta
    val estaEnPanico = restActivo.operacion.pausadoManualmente && pausaHasta > tiempoActualMs
    val estaAbiertoNormal = remember(restActivo) { validarRestauranteAbierto(restActivo) }
    val programadoActivo = restActivo.operacion.programadoActivo && globalProgramadoActivo
    val permiteProgramar = !estaAbiertoNormal && !estaEnPanico && programadoActivo
    val operacionActiva = estaAbiertoNormal && !estaEnPanico

    val (horaAbre, horaCierra) = remember(restActivo) {
        val ahora = Instant.fromEpochMilliseconds(getAhora()).toLocalDateTime(TimeZone.currentSystemDefault())
        val diaActual = ahora.dayOfWeek
        val horaActualStr = "${ahora.hour.toString().padStart(2, '0')}:${ahora.minute.toString().padStart(2, '0')}"

        val franjasDeHoy = when(diaActual) {
            DayOfWeek.MONDAY -> restActivo.horarios?.lunes ?: emptyList()
            DayOfWeek.TUESDAY -> restActivo.horarios?.martes ?: emptyList()
            DayOfWeek.WEDNESDAY -> restActivo.horarios?.miercoles ?: emptyList()
            DayOfWeek.THURSDAY -> restActivo.horarios?.jueves ?: emptyList()
            DayOfWeek.FRIDAY -> restActivo.horarios?.viernes ?: emptyList()
            DayOfWeek.SATURDAY -> restActivo.horarios?.sabado ?: emptyList()
            DayOfWeek.SUNDAY -> restActivo.horarios?.domingo ?: emptyList()
        }

        val franjasActivas = franjasDeHoy.filter { it.activo }.sortedBy { it.abre }
        val franjaActual = franjasActivas.firstOrNull { horaActualStr >= it.abre && horaActualStr <= it.cierra }

        if (franjaActual != null) Pair(franjaActual.abre, franjaActual.cierra)
        else {
            val proximaFranja = franjasActivas.firstOrNull { it.abre > horaActualStr }
            if (proximaFranja != null) Pair(proximaFranja.abre, proximaFranja.cierra)
            else Pair("Mañana", "")
        }
    }

    val usuario by com.yendo.mx.repository.AuthRepository.usuarioActual.collectAsState()
    val metodosBloqueadosUsuario = usuario?.metodos_pago_bloqueados ?: ""

    LaunchedEffect(restaurante.id) {
        viewModel.prepararParaRestaurante(restaurante.id)
        if (!esComercio) {
            viewModel.cargarMenuCompleto(restaurante.id)
        }
        try {
            val doc = dev.gitlive.firebase.Firebase.firestore.collection("Negocios").document(restaurante.id).get()
            if (doc.exists) {
                val fullDoc = doc.data<RestauranteFirebase>().copy(id = doc.id)
                restauranteFull = fullDoc
            }
        } catch (_: Exception) { }
    }

    LaunchedEffect(productoInicial) {
        if (productoInicial != null) {
            delay(150)
            if (operacionActiva || permiteProgramar) {
                if (CartManager.puedeAgregar(restaurante.id)) {
                    productoParaAdicionales.value = productoInicial
                    // 🚀 ANALÍTICA: Registro de vista de producto desde DeepLink o Inicio
                    AdAnalyticsManager.logItemView(
                        productId = productoInicial.id,
                        productName = productoInicial.nombre,
                        price = productoInicial.precio,
                        merchantName = restaurante.infoPublica.nombre
                    )
                } else {
                    productoPendienteCambio.value = productoInicial
                }
            }
        }
    }

    Scaffold(
        modifier = Modifier.fillMaxSize().navigationBarsPadding(),
        containerColor = colorFondoPantalla,
        floatingActionButton = { BotonCarritoFlotante { onVerCarrito(metodosBloqueadosUsuario) } },
        floatingActionButtonPosition = FabPosition.Center
    ) { padding ->
        PullToRefreshBox(
            isRefreshing = isRefreshing,
            onRefresh = { 
                scope.launch { 
                    isRefreshing = true
                    viewModel.reiniciarCarga()
                    if (!esComercio) {
                        viewModel.cargarMenuCompleto(restaurante.id)
                    }
                    delay(500) 
                    isRefreshing = false 
                } 
            },
            state = refreshState,
            modifier = Modifier.fillMaxSize().padding(padding)
        ) {
            LazyColumn(state = listState, modifier = Modifier.fillMaxSize()) {

                item(key = "header_global") {
                    Column {
                        Box(modifier = Modifier.fillMaxWidth().height(240.dp)) {
                            Box(modifier = Modifier.fillMaxWidth().height(180.dp)) {
                                if (restaurante.infoPublica.header.isNotEmpty()) {
                                    AsyncImage(
                                        model = ImageRequest.Builder(LocalPlatformContext.current).data(restaurante.infoPublica.header).crossfade(true).build(),
                                        contentDescription = "Portada", modifier = Modifier.fillMaxSize(), contentScale = ContentScale.Crop, filterQuality = FilterQuality.High
                                    )
                                    Box(modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.3f)))
                                } else {
                                    Box(modifier = Modifier.fillMaxSize().background(YendoGradient))
                                }
                                IconButton(onClick = onBack, modifier = Modifier.statusBarsPadding().padding(16.dp).background(colorFondoBotonBack, RoundedCornerShape(50)).size(36.dp)) {
                                    Icon(Icons.AutoMirrored.Filled.ArrowBack, null, tint = RosaYendo, modifier = Modifier.size(20.dp))
                                }
                            }
                            Box(modifier = Modifier.align(Alignment.BottomStart).padding(start = 16.dp).size(100.dp).shadow(elevation = 8.dp, shape = CircleShape).background(colorFondoBotonBack, shape = CircleShape)) {
                                Box(modifier = Modifier.fillMaxSize().padding(4.dp).clip(CircleShape).background(Color(0xFFEFEFEF))) {
                                    if (restaurante.infoPublica.logotipo.isNotEmpty()) {
                                        AsyncImage(
                                            model = ImageRequest.Builder(LocalPlatformContext.current).data(restaurante.infoPublica.logotipo).crossfade(false).build(),
                                            contentDescription = "Logo", modifier = Modifier.fillMaxSize(), contentScale = ContentScale.Crop, filterQuality = FilterQuality.High
                                        )
                                    }
                                }
                            }
                        }

                        Column(modifier = Modifier.padding(start = 16.dp, end = 16.dp, bottom = 8.dp, top = 8.dp)) {
                            BanderaPromocionNegocio(
                                tienePromo = restaurante.infoPublica.tienePromo,
                                promoTexto = restaurante.infoPublica.promoTexto,
                                modifier = Modifier.padding(bottom = 6.dp)
                            )

                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    text = restaurante.infoPublica.nombre.ifEmpty { "Restaurante" }, 
                                    color = colorTextoPrincipal, 
                                    fontWeight = FontWeight.ExtraBold, 
                                    fontSize = 28.sp,
                                    modifier = Modifier.weight(1f)
                                )
                                
                                // 🚀 INYECCIÓN: Botón de Favorito en el Header
                                com.yendo.mx.ui.components.BotonFavorito(
                                    id = restaurante.id,
                                    tipo = "RESTAURANTE",
                                    size = 40.dp,
                                    iconSize = 24.dp
                                )
                            }
                            
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 4.dp)) {
                                RestauranteRatingBadge(reputacion = restaurante.reputacion, forceWhiteText = isDark)
                                Text(" • ", fontSize = 14.sp, color = colorTextoSecundario)
                                Text(text = restaurante.infoPublica.categoria.ifEmpty { "Comida General" }, fontSize = 14.sp, color = colorTextoSecundario)
                            }

                            val (textoEstado, colorEstado) = when {
                                estaAbiertoNormal -> "Abierto • Cierra a las $horaCierra" to RosaYendo
                                horaAbre == "Mañana" -> "Cerrado por hoy" to Color.Red
                                else -> "Cerrado • Abre a las $horaAbre" to Color.Red
                            }
                            Text(text = textoEstado, color = colorEstado, fontWeight = FontWeight.Bold, fontSize = 14.sp)

                            // 🚀 BADGES DUALES SUTILES DE ENVÍO Y RECOGER SIN EMOJIS
                            val configZonaVal = com.yendo.mx.utils.ConfigManager.configActiva
                            val deliveryGlobalZonaActivo = (configZonaVal?.logistica?.delivery_activo ?: true) && com.yendo.mx.utils.LogisticaUtils.estaPlataformaAbierta(configZonaVal)
                            val permiteEnvio = operacionActiva && restaurante.operacion.deliveryActivo && (deliveryGlobalZonaActivo || restaurante.operacion.tieneFlotaPropiaActiva)
                            val permiteRecoger = operacionActiva && restaurante.operacion.pickupActivo

                            Row(
                                horizontalArrangement = Arrangement.spacedBy(8.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.padding(top = 8.dp)
                            ) {
                                if (permiteEnvio) {
                                    Surface(
                                        color = Color(0xFF2196F3).copy(alpha = 0.15f),
                                        shape = RoundedCornerShape(8.dp),
                                        border = BorderStroke(1.dp, Color(0xFF2196F3).copy(alpha = 0.4f))
                                    ) {
                                        Row(
                                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.TwoWheeler,
                                                contentDescription = "Envío",
                                                tint = Color(0xFF2196F3),
                                                modifier = Modifier.size(14.dp)
                                            )
                                            Spacer(Modifier.width(4.dp))
                                            Text(
                                                text = "Envío",
                                                color = Color(0xFF2196F3),
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 12.sp
                                            )
                                        }
                                    }
                                }

                                if (permiteRecoger) {
                                    Surface(
                                        color = Color(0xFFFF9800).copy(alpha = 0.15f),
                                        shape = RoundedCornerShape(8.dp),
                                        border = BorderStroke(1.dp, Color(0xFFFF9800).copy(alpha = 0.4f))
                                    ) {
                                        Row(
                                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 5.dp),
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Icon(
                                                imageVector = Icons.Default.Storefront,
                                                contentDescription = "Recoger",
                                                tint = Color(0xFFFF9800),
                                                modifier = Modifier.size(14.dp)
                                            )
                                            Spacer(Modifier.width(4.dp))
                                            Text(
                                                text = "Recoger",
                                                color = Color(0xFFFF9800),
                                                fontWeight = FontWeight.Bold,
                                                fontSize = 12.sp
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        if (!estaAbiertoNormal && permiteProgramar) {
                            Card(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), colors = CardDefaults.cardColors(containerColor = colorFondoAlertaInfo), border = BorderStroke(1.dp, colorBordeAlertaInfo)) {
                                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Default.Schedule, null, tint = Color(0xFF1976D2))
                                    Spacer(Modifier.width(8.dp))
                                    val textoBanner = if (horaAbre == "Mañana") "Cerrado por hoy. Programa tu pedido para mañana." else "Cerrado. Programa tu pedido a partir de las $horaAbre."
                                    Text(textoBanner, color = Color(0xFF1976D2), fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                }
                            }
                        } else if (!estaAbiertoNormal && !programadoActivo) {
                            Card(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), colors = CardDefaults.cardColors(containerColor = colorFondoAlertaPausado), border = BorderStroke(1.dp, colorBordeAlertaPausado)) {
                                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(Icons.Default.Storefront, null, tint = Color.Red)
                                    Spacer(Modifier.width(8.dp))
                                    Text("Cerrado. Vuelve a visitarnos en nuestro horario de servicio.", color = Color.Red, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                                }
                            }
                        }

                        OutlinedTextField(
                            value = searchQuery.value,
                            onValueChange = { searchQuery.value = it },
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                            placeholder = { Text("Buscar en el menú...", color = colorTextoSecundario) },
                            leadingIcon = { Icon(Icons.Default.Search, null, tint = RosaYendo) },
                            trailingIcon = { if (searchQuery.value.isNotEmpty()) IconButton(onClick = { searchQuery.value = "" }) { Icon(Icons.Default.Clear, null, tint = colorTextoSecundario) } },
                            shape = RoundedCornerShape(12.dp),
                            colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = RosaYendo, unfocusedBorderColor = colorBordeSearch, focusedContainerColor = colorFondoSearch, unfocusedContainerColor = colorFondoSearch, focusedTextColor = colorTextoPrincipal, unfocusedTextColor = colorTextoPrincipal, cursorColor = RosaYendo),
                            singleLine = true
                        )
                    }
                }

                if (searchQuery.value.isBlank() && categoriasKeys.isNotEmpty()) {
                    stickyHeader(key = "sticky_tabs") {
                        Surface(
                            color = colorFondoPantalla,
                            modifier = Modifier.fillMaxWidth().shadow(if(firstVisibleIndex > 0) 4.dp else 0.dp)
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 8.dp)) {
                                LazyRow(state = tabsListState, horizontalArrangement = Arrangement.spacedBy(8.dp), contentPadding = PaddingValues(horizontal = 16.dp)) {
                                    item {
                                        Surface(
                                            onClick = { mostrarMenuCategorias = true },
                                            shape = RoundedCornerShape(12.dp),
                                            color = colorFondoSearch,
                                            border = BorderStroke(1.dp, colorBordeSearch),
                                            modifier = Modifier.padding(end = 4.dp)
                                        ) {
                                            Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                                                Icon(Icons.Default.KeyboardArrowDown, null, modifier = Modifier.size(18.dp), tint = colorTextoPrincipal)
                                                Spacer(Modifier.width(4.dp))
                                                Text("Menú", fontWeight = FontWeight.Black, fontSize = 13.sp, color = colorTextoPrincipal)
                                            }
                                        }
                                    }

                                    items(categoriasKeys) { cat ->
                                        val isSelected = cat == categoriaSeleccionada
                                        Surface(
                                            onClick = {
                                                categoriaSeleccionada = cat
                                                scope.launch { categoryIndices[cat]?.let { listState.animateScrollToItem(it) } }
                                            },
                                            shape = CircleShape,
                                            color = if (isSelected) RosaYendo else colorChipInactivo,
                                            contentColor = if (isSelected) Color.White else colorTextoSecundario
                                        ) {
                                            Text(
                                                text = cat,
                                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 8.dp),
                                                fontSize = 13.sp,
                                                fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    if (esComercio) {
                        items(categoriasKeys) { cat ->
                            val state = viewModel.obtenerEstadoCategoria(restaurante.id, cat)

                            LaunchedEffect(cat, restaurante.id) {
                                // 🚀 ESCUDO ANTIFUGA: Esperamos a que el scroll se estabilice
                                // Si el usuario pasa de largo, este efecto se cancela y ahorramos lecturas.
                                delay(150)
                                viewModel.cargarProductosPorCategoria(idRestaurante = restaurante.id, categoria = cat)
                            }

                            if (state.products.isNotEmpty() || state.isLoading) {
                                Column(modifier = Modifier.padding(bottom = 24.dp)) {
                                    Text(
                                        text = cat,
                                        fontSize = 20.sp,
                                        fontWeight = FontWeight.Black,
                                        color = colorTextoPrincipal,
                                        modifier = Modifier.padding(horizontal = 16.dp, vertical = 12.dp)
                                    )

                                    if (state.isLoading && state.products.isEmpty()) {
                                        Box(modifier = Modifier.fillMaxWidth().height(180.dp), contentAlignment = Alignment.Center) {
                                            CircularProgressIndicator(color = RosaYendo, modifier = Modifier.size(24.dp))
                                        }
                                    } else {
                                        val horizontalState = rememberLazyListState()
                                        val endReached by remember {
                                            derivedStateOf {
                                                val lastVisible = horizontalState.layoutInfo.visibleItemsInfo.lastOrNull()
                                                lastVisible != null && lastVisible.index >= state.products.size - 5
                                            }
                                        }

                                        LaunchedEffect(endReached) {
                                            if (endReached && !state.isLoading && !state.isLastPage) {
                                                viewModel.cargarSiguientePagina(restaurante.id, cat)
                                            }
                                        }

                                        LazyRow(
                                            state = horizontalState,
                                            contentPadding = PaddingValues(horizontal = 16.dp),
                                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                                            modifier = Modifier.fillMaxWidth()
                                        ) {
                                            items(state.products) { producto ->
                                                CardProductoCuadradoV2(
                                                    producto = producto,
                                                    modifier = Modifier.width(160.dp),
                                                    onItemClick = {
                                                        if (operacionActiva || permiteProgramar) {
                                                            if (CartManager.puedeAgregar(restaurante.id)) {
                                                                productoParaAdicionales.value = producto
                                                                // 🚀 ANALÍTICA: Registro de vista de producto
                                                                AdAnalyticsManager.logItemView(
                                                                    productId = producto.id,
                                                                    productName = producto.nombre,
                                                                    price = producto.precio,
                                                                    merchantName = restaurante.infoPublica.nombre
                                                                )
                                                            } else {
                                                                productoPendienteCambio.value = producto
                                                            }
                                                        }
                                                    }
                                                )
                                            }
                                            if (state.isLoading) {
                                                item {
                                                    Box(modifier = Modifier.size(160.dp), contentAlignment = Alignment.Center) {
                                                        CircularProgressIndicator(color = RosaYendo, modifier = Modifier.size(20.dp))
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        // 🚀 MODO RESTAURANTE: Paginación Vertical Inteligente
                        categoriasKeys.forEach { cat ->
                            val state = viewModel.obtenerEstadoCategoria(restaurante.id, cat)
                            val prods = state.products
                            
                            // Trigger de carga inicial (Invisible)
                            item(key = "trigger_${restaurante.id}_$cat") {
                                LaunchedEffect(cat, restaurante.id) {
                                    delay(150)
                                    viewModel.cargarProductosPorCategoria(restaurante.id, cat)
                                }
                            }

                            if (prods.isNotEmpty()) {
                                item(key = "header_cat_$cat") {
                                    Text(
                                        text = cat,
                                        fontSize = 20.sp,
                                        fontWeight = FontWeight.Black,
                                        color = colorTextoPrincipal,
                                        modifier = Modifier.padding(start = 16.dp, end = 16.dp, top = 24.dp, bottom = 12.dp)
                                    )
                                }

                                val filas = prods.chunked(2)
                                itemsIndexed(filas, key = { index, _ -> "fila_${cat}_$index" }) { index, fila ->
                                    // 2. Disparador de Paginación Infinita (Vertical)
                                    LaunchedEffect(index) {
                                        if (index >= filas.size - 2 && !state.isLoading && !state.isLastPage) {
                                            viewModel.cargarSiguientePagina(restaurante.id, cat)
                                        }
                                    }

                                    Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                        fila.forEach { producto ->
                                            CardProductoCuadradoV2(
                                                producto = producto,
                                                modifier = Modifier.weight(1f),
                                                onItemClick = {
                                                    if (operacionActiva || permiteProgramar) {
                                                        if (CartManager.puedeAgregar(restaurante.id)) productoParaAdicionales.value = producto
                                                        else productoPendienteCambio.value = producto
                                                    }
                                                }
                                            )
                                        }
                                        if (fila.size == 1) Spacer(modifier = Modifier.weight(1f))
                                    }
                                }

                                if (state.isLoading) {
                                    item {
                                        Box(modifier = Modifier.fillMaxWidth().height(60.dp), contentAlignment = Alignment.Center) {
                                            CircularProgressIndicator(color = RosaYendo, modifier = Modifier.size(20.dp))
                                        }
                                    }
                                }
                            } else if (state.isLoading) {
                                item {
                                    Box(modifier = Modifier.fillMaxWidth().height(150.dp), contentAlignment = Alignment.Center) {
                                        CircularProgressIndicator(color = RosaYendo, modifier = Modifier.size(24.dp))
                                    }
                                }
                            }
                        }
                    }
                    item { Spacer(Modifier.height(140.dp)) }

                } else if (searchQuery.value.isNotEmpty()) {
                    // -------------------------------------------------------------
                    // MODO BÚSQUEDA ACTIVA (Oculta categorías)
                    // -------------------------------------------------------------
                    item { Text("Resultados para '${searchQuery.value}'", fontWeight = FontWeight.Bold, color = colorTextoSecundario, modifier = Modifier.padding(16.dp)) }

                    if (isSearchingAlgolia) {
                        item { Box(modifier = Modifier.fillMaxWidth().height(150.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator(color = RosaYendo) } }
                    } else {
                        // 🚀 RESILIENCIA DUAL (ALGOLIA + FIRESTORE):
                        // Si Algolia devuelve resultados (con Tolerancia a Errores de Dedo / Typo Tolerance), los usamos.
                        // Si Algolia devuelve 0 hits (ej. producto aún no indexado), hacemos fallback a los productos
                        // ya cargados de Firestore para que el cliente NUNCA reciba 0 resultados por error.
                        val productosFiltrados = if (searchQuery.value.length >= 3 && productosParaBusqueda.isNotEmpty()) {
                            productosParaBusqueda
                        } else {
                            productosMenu.filter { 
                                it.nombre.contains(searchQuery.value, ignoreCase = true) ||
                                it.categoria.contains(searchQuery.value, ignoreCase = true) ||
                                it.descripcion.contains(searchQuery.value, ignoreCase = true)
                            }
                        }

                        if (productosFiltrados.isEmpty()) {
                            item { Box(modifier = Modifier.fillMaxWidth().height(150.dp), contentAlignment = Alignment.Center) { Text("No se encontraron productos.", color = colorTextoSecundario) } }
                        } else {
                            items(productosFiltrados.chunked(2)) { fila ->
                                Row(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp), horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                                    fila.forEach { producto ->
                                        CardProductoCuadradoV2(
                                            producto = producto, modifier = Modifier.weight(1f),
                                            onItemClick = {
                                                if (operacionActiva || permiteProgramar) {
                                                    if (CartManager.puedeAgregar(restaurante.id)) {
                                                        productoParaAdicionales.value = producto
                                                        // 🚀 ANALÍTICA: Registro de vista de producto
                                                        AdAnalyticsManager.logItemView(
                                                            productId = producto.id,
                                                            productName = producto.nombre,
                                                            price = producto.precio,
                                                            merchantName = restaurante.infoPublica.nombre
                                                        )
                                                    } else {
                                                        productoPendienteCambio.value = producto
                                                    }
                                                }
                                            }
                                        )
                                    }
                                    if (fila.size == 1) Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }
                    }
                    item { Spacer(Modifier.height(140.dp)) }
                }
            }
        }
    }

    // ==========================================
    // 🚀 BOTTOM SHEET DE CATEGORÍAS (RAPPI)
    // ==========================================
    if (mostrarMenuCategorias) {
        ModalBottomSheet(
            onDismissRequest = { mostrarMenuCategorias = false },
            containerColor = colorFondoDialogo,
            shape = RoundedCornerShape(topStart = 24.dp, topEnd = 24.dp)
        ) {
            LazyColumn(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp)) {
                item {
                    Text("Todas las categorías", fontWeight = FontWeight.Black, fontSize = 22.sp, color = colorTextoPrincipal, modifier = Modifier.padding(bottom = 16.dp))
                }
                items(categoriasKeys) { cat ->
                    val isSelected = cat == categoriaSeleccionada
                    TextButton(
                        onClick = {
                            mostrarMenuCategorias = false
                            categoriaSeleccionada = cat
                            scope.launch { categoryIndices[cat]?.let { listState.animateScrollToItem(it) } }
                        },
                        modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.textButtonColors(
                            containerColor = if (isSelected) RosaYendo.copy(alpha = 0.1f) else Color.Transparent
                        )
                    ) {
                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            Text(cat, color = if (isSelected) RosaYendo else colorTextoPrincipal, fontWeight = if (isSelected) FontWeight.Black else FontWeight.Normal, fontSize = 16.sp)
                            if (isSelected) Icon(Icons.Default.Star, null, tint = RosaYendo, modifier = Modifier.size(16.dp))
                        }
                    }
                }
                item { Spacer(Modifier.height(40.dp).navigationBarsPadding()) }
            }
        }
    }

    // --- SELECTOR DE ADICIONALES ---
    if (productoParaAdicionales.value != null) {
        SelectorAdicionales(
            producto = productoParaAdicionales.value!!,
            onDismiss = { productoParaAdicionales.value = null },
            onConfirmar = { prod, cant, nota, extras, modo ->
                CartManager.agregarAlCarrito(
                    producto = prod, cantidad = cant, notas = nota, adicionales = extras, modoCompra = modo, 
                    idRestaurante = restaurante.id, 
                    idZona = restaurante.ubicacion.idZona, // 🚀 PASAMOS EL ID DE LA ZONA DEL RESTAURANTE
                    nombreRestaurante = restaurante.infoPublica.nombre.ifEmpty { "Negocio Yendo" },
                    logoRestaurante = restaurante.infoPublica.logotipo,
                    direccionRestaurante = restaurante.ubicacion.direccion, lat = restaurante.ubicacion.latitud, lng = restaurante.ubicacion.longitud, metodosPagoAceptados = restaurante.metodosPagoAceptadosFinal,
                    deliveryActivo = restaurante.operacion.deliveryActivo, pickupActivo = restaurante.operacion.pickupActivo, programadoActivo = restaurante.operacion.programadoActivo,
                    bancoRestaurante = restaurante.datosBancariosFinal?.banco, cuentaRestaurante = restaurante.datosBancariosFinal?.cuenta, beneficiarioRestaurante = restaurante.datosBancariosFinal?.beneficiario,
                    deudaRestaurante = restaurante.finanzasLocales.balanceActualDeuda, limiteDeudaRestaurante = restaurante.finanzasLocales.limiteDeudaPersonalizado, balanceExcedidoRestaurante = restaurante.operacion.balanceExcedido,
                    pausadoManualmente = restaurante.operacion.pausadoManualmente, pausaHasta = restaurante.operacion.pausaHasta, horarios = restaurante.horarios, pedidoMinimoRestaurante = restaurante.operacion.montoMinimoCompra.toDouble(),
                    comisionRestaurante = restaurante.finanzasLocales.comisionPersonalizada.toDouble(),
                    tipoNegocio = restaurante.infoPublica.tipoNegocio,
                    pagoPostArmado = restaurante.operacion.pagoPostArmado

                )
                productoParaAdicionales.value = null
            }
        )
    }

    // --- ALERTA: OTRO RESTAURANTE EN CARRITO ---
    if (productoPendienteCambio.value != null) {
        AlertDialog(
            onDismissRequest = { productoPendienteCambio.value = null },
            title = { Text("¿Empezar un nuevo pedido?", fontWeight = FontWeight.Bold, color = colorTextoPrincipal) },
            text = { Text("Actualmente tienes productos de otro restaurante en tu carrito. Si continúas, se vaciará tu carrito actual.", color = colorTextoSecundario) },
            confirmButton = { Button(onClick = { CartManager.vaciarCarrito(); productoParaAdicionales.value = productoPendienteCambio.value; productoPendienteCambio.value = null }, colors = ButtonDefaults.buttonColors(containerColor = RosaYendo)) { Text("Vaciar y continuar", fontWeight = FontWeight.Bold, color = Color.White) } },
            dismissButton = { TextButton(onClick = { productoPendienteCambio.value = null }) { Text("Cancelar", color = colorTextoSecundario, fontWeight = FontWeight.Bold) } },
            containerColor = colorFondoDialogo, shape = RoundedCornerShape(16.dp)
        )
    }
}

@Composable
fun CardProductoCuadradoV2(producto: Producto, modifier: Modifier, onItemClick: () -> Unit) {
    val isDark = isSystemInDarkTheme()
    val colorBgImg = if (isDark) Color(0xFF121212) else Color.White // 🚀 CAMBIADO: Match con el fondo global
    val colorTexto = if (isDark) Color.White else Color.DarkGray

    Column(modifier = modifier.clickable { onItemClick() }) {
        Box(modifier = Modifier.fillMaxWidth().aspectRatio(1f).clip(RoundedCornerShape(16.dp)).background(colorBgImg)) {
            if (producto.foto.isNotEmpty()) {
                AsyncImage(
                    model = ImageRequest.Builder(LocalPlatformContext.current).data(producto.foto).crossfade(true).build(),
                    contentDescription = producto.nombre,
                    modifier = Modifier.fillMaxSize(),
                    contentScale = ContentScale.Crop, // 🚀 RESTAURADO: Crop para llenar el espacio
                    filterQuality = FilterQuality.High
                )
            } else {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { Text("Yendo", color = Color.LightGray, fontWeight = FontWeight.Bold) }
            }

            if (producto.infoProducto.tipoPromocion.isNotBlank() && producto.infoProducto.tipoPromocion.uppercase() != "NINGUNA" || producto.infoProducto.precioOriginal > producto.precio) {
                Box(modifier = Modifier.align(Alignment.TopStart).padding(8.dp).background(Color(0xFFE53935), RoundedCornerShape(8.dp))) {
                    val textoEtiqueta = when {
                        producto.infoProducto.tipoPromocion.uppercase() == "DOS_POR_UNO" -> "2x1"
                        producto.infoProducto.tipoPromocion.uppercase() == "TRES_POR_DOS" -> "3x2"
                        producto.infoProducto.tipoPromocion.uppercase() == "REGALO" -> "🎁 + REGALO"
                        producto.infoProducto.precioOriginal > producto.precio -> {
                            val descuentoPorcentaje = ((1 - (producto.precio / producto.infoProducto.precioOriginal)) * 100).toInt()
                            "-$descuentoPorcentaje%"
                        }
                        else -> "PROMO"
                    }

                    Text(
                        text = textoEtiqueta,
                        color = Color.White,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Black,
                        modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp)
                    )
                }
            }

            // 🚀 BOTON DE AGREGAR (+) EN EL CATALOGO (Mejora UX)
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(8.dp)
                    .size(32.dp)
                    .background(RosaYendo, CircleShape)
                    .border(2.dp, Color.White, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(Icons.Default.Add, null, tint = Color.White, modifier = Modifier.size(20.dp))
            }

            // 🚀 INYECCIÓN: Botón de Favorito en Producto
            Box(modifier = Modifier.align(Alignment.TopEnd).padding(8.dp)) {
                com.yendo.mx.ui.components.BotonFavorito(
                    id = producto.id,
                    tipo = "PRODUCTO",
                    idRestauranteAsociado = producto.idRestaurante,
                    size = 28.dp,
                    iconSize = 16.dp
                )
            }
        }
        Column(modifier = Modifier.padding(top = 6.dp, start = 4.dp, end = 4.dp)) {
            // 1. PRECIO PEGADO A LA TARJETA/IMAGEN
            Row(verticalAlignment = Alignment.CenterVertically) {
                val etiquetaPrecio = if (producto.precioPor == "LITRO") "L" else "kg"
                val textoPrecioReferencia = when (producto.tipoVenta) {
                    "PIEZA_POR_PESO", "PESO" -> "$${formatDecimal(producto.precio, 2)} / $etiquetaPrecio"
                    else -> "$${formatDecimal(producto.precio, 2)} / un."
                }
                
                Text(text = textoPrecioReferencia, color = RosaYendo, fontWeight = FontWeight.ExtraBold, fontSize = 14.sp)

                if (producto.infoProducto.precioOriginal > producto.precio) {
                    Spacer(Modifier.width(6.dp))
                    Text(
                        text = "$${producto.infoProducto.precioOriginal}",
                        color = Color.Gray,
                        fontSize = 11.sp,
                        textDecoration = TextDecoration.LineThrough
                    )
                }
            }

            // 2. NOMBRE DEL PRODUCTO ABAJO DEL PRECIO (HASTA 2 LÍNEAS ALINEADO)
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = producto.nombre,
                fontWeight = FontWeight.Bold,
                fontSize = 13.sp,
                minLines = 2,
                maxLines = 2,
                lineHeight = 17.sp,
                overflow = TextOverflow.Ellipsis,
                color = colorTexto
            )

            // 3. REFERENCIA DE PESO SI APLICA
            if ((producto.tipoVenta == "PIEZA_POR_PESO" || producto.tipoVenta == "PESO") && producto.pesoEstimadoKg > 0.0) {
                val etiqueta = if (producto.precioPor == "LITRO") "L" else "kg"
                val sufijo = if (producto.tipoVenta == "PIEZA_POR_PESO") "pieza" else "porción"
                Text(
                    text = "(Aprox. ${producto.pesoEstimadoKg} $etiqueta por $sufijo)",
                    color = Color.Gray,
                    fontSize = 10.sp
                )
            }
        }
    }
}

@Composable
fun BanderaPromocionNegocio(tienePromo: Boolean, promoTexto: String, modifier: Modifier = Modifier) {
    val esPromoValida = tienePromo && promoTexto.isNotBlank() && promoTexto.uppercase() != "NINGUNA"

    if (esPromoValida) {
        val textoAVisualizar = if (promoTexto.uppercase() == "REGALO") "+ UN REGALO 🎁" else promoTexto.replace("_", " ").uppercase()

        Row(
            modifier = modifier
                .background(Color(0xFFFF5722), RoundedCornerShape(topEnd = 8.dp, bottomEnd = 8.dp))
                .padding(horizontal = 10.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Icon(Icons.Default.LocalOffer, null, tint = Color.White, modifier = Modifier.size(12.dp))
            Text(textoAVisualizar, color = Color.White, fontSize = 10.sp, fontWeight = FontWeight.Black)
        }
    }
}
